import { LE } from "@/constants/enums";
import { formDetailsMapping, type FormSubmission, type FormSubmissionSelect } from "./formSubmission";
import { ChangeOfOperatorApplication } from "./formTagsAndMappings/changeOfOperator";
import { DrainageInjectionWellPermitRenewalApplication } from "./formTagsAndMappings/drainageInjectionWellPermitRenewal";
import type { GroupControl } from "./parsedFormSubmission";
import type { ExistingDrainageInjectionWell } from "./formTagsAndMappings/existingDrainageInjectionWell";
import type { GeneralOperating } from "./formTagsAndMappings/generalOperating";
import type { InjectionWellAbandonment } from "./formTagsAndMappings/injectionWellAbandonment";
import type { DrainageInjectionWellGeneral } from "./formTagsAndMappings/drainageInjectionWellGeneral";
import type { InjectionWellRenewal } from "./formTagsAndMappings/injectionWellRenewal";
import type { ExistingInjectionWell } from "./formTagsAndMappings/existingInjectionWell";
import type { FacilityNameChange } from "./formTagsAndMappings/facilityNameChange";

export default {
  addAuthRequestFields,
  skinnyResponseAdapter,
  responseAdapter,
  requestAdapter,
};

function addAuthRequestFields(entity: FormSubmission) {
  entity._authRequestFields = ["id", "assignedToMurn", "statusMurn"];
  return entity;
}

/** Be sure to await this when using it. Commonly used for lists and child lists */
async function skinnyResponseAdapter(response: { data: FormSubmission }) {
  try {
    if (typeof response.data.formSubmissionJson === "string") {
      response.data.parsedFormSubmission = JSON.parse(response.data.formSubmissionJson);
    } else {
      response.data.parsedFormSubmission = response.data.formSubmissionJson;
    }
  } catch (error) {
    response.data.parsedFormSubmission = undefined;
    console.error("Invalid JSON for form submission data", error);
  }

  addAuthRequestFields(response.data);

  return response.data;
}

function requestAdapter(data: FormSubmission) {
  return data;
}

/** Be sure to await this when using it. */
async function responseAdapter(response: { data: FormSubmission }) {
  await skinnyResponseAdapter(response);

  switch (response.data.formNameMurn) {
    case LE.FormName.DrainageInjectionWellRenewal:
      response.data._drainageInjectionWellPermitRenewalApplication = getFormDetailsByFormType(response.data);
      break;
    case LE.FormName.ChangeOfOperator:
      response.data._changeOfOperatorApplication = getFormDetailsByFormType(response.data);
      break;
    case LE.FormName.ExistingDrainageInjectionWell:
      response.data._existingDrainageInjectionWellApplication = getFormDetailsByFormType(response.data);
      break;
    case LE.FormName.GeneralOperating:
      response.data._generalOperatingApplication = getFormDetailsByFormType(response.data);
      break;
    case LE.FormName.InjectionWellAbandonment:
      response.data._injectionWellAbandonmentApplication = getFormDetailsByFormType(response.data);
      break;
    case LE.FormName.DrainageInjectionWellGeneral:
      response.data._drainageInjectionWellGeneralApplication = getFormDetailsByFormType(response.data);
      break;
    case LE.FormName.InjectionWellRenewal:
      response.data._injectionWellRenewalApplication = getFormDetailsByFormType(response.data);
      break;
    case LE.FormName.ExistingInjectionWell:
      response.data._existingInjectionWellApplication = getFormDetailsByFormType(response.data);
      break;
    case LE.FormName.FacilityNameChange:
      response.data._facilityNameChangeApplication = getFormDetailsByFormType(response.data);
      break;
    default:
      console.error("No form mapping exists for this form type");
      break;
  }

  return response.data;
}

function getFormDetailsByFormType(data: FormSubmission) {
  let applicationObj: any = {};
  const formMapping = formDetailsMapping.get(data.formNameMurn);
  if (formMapping) {
    formMapping.sections.forEach((x) => {
      const sectionData = getSectionData(data, x);
      applicationObj[x.sectionPropertyName] = x.sectionIsList ? sectionData : sectionData[0];
    });
  }
  return applicationObj;
}

function getSectionData(
  data: FormSubmission,
  sectionMapping:
    | DrainageInjectionWellPermitRenewalApplication.SectionMapping
    | ChangeOfOperatorApplication.SectionMapping
    | ExistingDrainageInjectionWell.SectionMapping
    | GeneralOperating.SectionMapping
    | InjectionWellAbandonment.SectionMapping
    | DrainageInjectionWellGeneral.SectionMapping
    | InjectionWellRenewal.SectionMapping
    | ExistingInjectionWell.SectionMapping
    | FacilityNameChange.SectionMapping,
) {
  const fieldsArray: any = [];

  if (data.parsedFormSubmission?.sections) {
    const currentSection = data.parsedFormSubmission.sections.find((x) => x.tag === sectionMapping.sectionTag);
    if (!currentSection) {
      console.error("Could not find section: ", sectionMapping.sectionTag);
      return fieldsArray;
    }

    for (let group of currentSection.groups) {
      let groupItemData: any = {};
      for (let property of sectionMapping.sectionProperties) {
        let control = group.controls.find((x) => x.tag === property.propertyTag);
        if (control) {
          groupItemData[property.propertyName] = getControlValueBasedOnType(control);
        }
      }

      fieldsArray.push(groupItemData);
    }
  }

  return fieldsArray;
}

function getControlValueBasedOnType(control: GroupControl) {
  switch (control.type) {
    case "text":
      return control.value ?? undefined;
    case "number":
      return control.value ?? undefined;
    case "date":
      return control.value ?? undefined;
    case "select":
      return control.value ?? undefined;
    case "multi-select":
      return control.value ?? undefined;
    case "email":
      return control.value ?? undefined;
    case "name":
      return { fullName: control.value?.fullName, title: control.value?.title };
    case "address":
      return {
        street: control.value?.street,
        street2: control.value?.street2,
        city: control.value?.city,
        state: control.value?.state,
        zip: control.value?.zip,
      };
    case "location":
      return {
        latitude: control.value?.latitude,
        longitude: control.value?.longitude,
      };
    case "contact":
      return control.value ?? undefined;
    default:
      console.warn("CASE FOR VALUE TYPE NOT FOUND: ", control.type);
      return control.value;
  }
}

export const getFormattedSelectValue = (value: FormSubmissionSelect | undefined) => {
  if (value?.description === "Other" && value.other) {
    return value.description + " - " + value.other;
  } else {
    return value?.description;
  }
};
